/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import {TableHeaderModel} from '../models/table-header.model';
import {TableSortedByModel} from '../models/table-sorted-by.model';
import {SortDirection} from '../../../api/generic/contracts/sort-direction.enum';
import {TableHeaderStatusModel} from '../models/table-header-status.model';
import {StatefulComponent} from '../../stateful/stateful.component';
import {KeyValue} from '@angular/common';
import {TableHeaderEntryModel} from '../models/table-header-entry.model';

@Component({
    selector: 'app-table-header',
    templateUrl: './table-header.component.html',
    styleUrls: ['./../table-global-styles.css', './table-header.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TableHeaderComponent<Enum> extends StatefulComponent<TableHeaderStatusModel<Enum>> implements AfterViewInit {

    /**
     * Headers object
     */
    @Input() headers: TableHeaderModel<Enum> = {} as TableHeaderModel<Enum>;
    /**
     * Is Expandable Lines
     */
    @Input() expandableLines: boolean;
    /**
     * The columns template class to be applied to the grid component
     */
    @Input() columnStyles: { [key: string]: any };
    /**
     * Initial sorting status
     */
    @Input() initiallySortedBy: TableSortedByModel<Enum>;
    @Input() excludedColumns: Enum[] = [];
    /**
     * Event emitter for when the sort status is changed
     * */
    @Output() sortedBy = new EventEmitter<TableSortedByModel<Enum>>();
    /**
     * Sort direction enum
     */
    public sortDirection = SortDirection;

    ngAfterViewInit(): void {
        this.restoreState({sortedBy: this.initiallySortedBy});
    }

    restoreState(state: TableHeaderStatusModel<Enum>): void {
        this.storeState(state);
        this.sortedBy.emit(this.state.sortedBy);
    }

    /**
     * Manages the sortedBy status change
     * @param tableEntry The header in which the sort by status is changed
     */
    sortByStatusChange(tableEntry: TableHeaderEntryModel<Enum>): void {
        if (tableEntry.sortable) {
            if (this.state?.sortedBy?.code === tableEntry.code) {
                this.storeState({
                    sortedBy: {
                        code: tableEntry.code,
                        direction: this.state.sortedBy.direction === SortDirection.DESC ?
                                SortDirection.ASC : SortDirection.DESC
                    }
                });
            } else {
                this.storeState({sortedBy: {code: tableEntry.code, direction: SortDirection.DESC}});
            }
            this.sortedBy.emit(this.state.sortedBy);
        }
    }

    /**
     * Returns if the header sorting arrow should be showed
     */
    showSortingArrow(tableEntry: TableHeaderEntryModel<Enum>): boolean {
        return tableEntry.sortable && tableEntry.code === this.state?.sortedBy?.code;
    }

    /**
     * Function for the ngFor to keep the original order in each the items were added to the {@link TableLineModel} data map
     */
    originalOrder(..._: KeyValue<Enum, TableHeaderEntryModel<Enum>>[]): number {
        return 0;
    }

    /**
     * Returns the data-cy for the column name
     */
    getDataCyHeaderColumnName(name: string): string {
        return `column-name-${name}`;
    }

    /**
     * Returns the data-cy for the sorting arrow
     */
    getDataCySortingArrow(name: string): string {
        return `table-header-${name}-sorting-arrow`;
    }

    /**
     * Returns the data-cy for the sorting arrow button
     */
    getDataCySortingArrowButton(name: string): string {
        return `table-header-${name}-sorting-arrow-button`;
    }

    showColumn(key: string): boolean {
        return !this.excludedColumns.map((column) => column.toString())
                .includes(key);
    }
}
